import { Box, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Switch, TextField, Typography } from '@mui/material';
import { DayOfWeek, EmailScheduleFrequency } from '../../../Generated/Mark-It-View-Api';
import { EmailScheduleDraft, EmailScheduleDraftErrors } from './EmailScheduleDraft';
import RecipientsInput from './RecipientsInput';

interface EmailScheduleFieldsProps {
  draft: EmailScheduleDraft;
  errors: EmailScheduleDraftErrors;
  disabled?: boolean;
  onChange: (draft: EmailScheduleDraft) => void;
}

const FREQUENCY_LABELS: Record<EmailScheduleFrequency, string> = {
  Daily: 'Every day',
  Weekdays: 'Weekdays',
  Weekly: 'Weekly',
  Monthly: 'Monthly',
};

export default function EmailScheduleFields(props: EmailScheduleFieldsProps) {
  const { draft, errors, disabled, onChange } = props;

  const update = (patch: Partial<EmailScheduleDraft>) => onChange({ ...draft, ...patch });

  return (
    <Box sx={{ mt: 2 }}>
      <FormControlLabel
        control={<Switch checked={draft.enabled} disabled={disabled} onChange={(e) => update({ enabled: e.target.checked })} />}
        label="Email this query on a schedule"
      />

      {draft.enabled && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
          <RecipientsInput value={draft.recipients} onChange={(recipients) => update({ recipients })} error={errors.recipients} disabled={disabled} />

          <TextField
            label="Subject (optional)"
            variant="standard"
            fullWidth
            disabled={disabled}
            value={draft.subject}
            onChange={(e) => update({ subject: e.target.value })}
            helperText="Defaults to the query name and quote date"
          />

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl variant="standard" sx={{ minWidth: 160 }} disabled={disabled}>
              <InputLabel id="email-schedule-frequency-label">Frequency</InputLabel>
              <Select
                labelId="email-schedule-frequency-label"
                value={draft.frequency}
                onChange={(e) => update({ frequency: e.target.value as EmailScheduleFrequency })}
              >
                {Object.values(EmailScheduleFrequency).map((frequency) => (
                  <MenuItem key={frequency} value={frequency}>
                    {FREQUENCY_LABELS[frequency]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {draft.frequency === EmailScheduleFrequency.Weekly && (
              <FormControl variant="standard" sx={{ minWidth: 160 }} disabled={disabled}>
                <InputLabel id="email-schedule-day-of-week-label">Day</InputLabel>
                <Select
                  labelId="email-schedule-day-of-week-label"
                  value={draft.dayOfWeek}
                  onChange={(e) => update({ dayOfWeek: e.target.value as DayOfWeek })}
                >
                  {Object.values(DayOfWeek).map((day) => (
                    <MenuItem key={day} value={day}>
                      {day}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {draft.frequency === EmailScheduleFrequency.Monthly && (
              <TextField
                label="Day of month"
                type="number"
                variant="standard"
                disabled={disabled}
                inputProps={{ min: 1, max: 31 }}
                value={draft.dayOfMonth}
                onChange={(e) => update({ dayOfMonth: Number(e.target.value) })}
                error={!!errors.dayOfMonth}
                helperText={errors.dayOfMonth ?? 'Short months send on their last day'}
                sx={{ width: 160 }}
              />
            )}

            <TextField
              label="Time"
              type="time"
              variant="standard"
              disabled={disabled}
              value={draft.timeOfDay}
              onChange={(e) => update({ timeOfDay: e.target.value })}
              error={!!errors.timeOfDay}
              helperText={errors.timeOfDay}
              inputProps={{ step: 900 }}
              sx={{ width: 140 }}
            />
          </Box>

          <Typography variant="caption" color="text.secondary">
            Times are in {draft.timeZoneId}. The email attaches the query results as an Excel workbook, run against the latest quote date.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
